import moment from 'moment';
import { mapState, mapMutations } from 'vuex';
import { getName as expenseTypeName } from '@/data/expense-types';
import { getLabel as getGuideStatusLabel } from '@/data/billing-account-statuses';
import hasIssuedFiscalDocument from '@/components/fiscal-document/hasIssuedDocument';
import FiscalDocument from '@/components/fiscal-document/FiscalDocument.vue';
import {
  isEnabled as isInsuranceBillingEnabled,
  idOf,
  byAppointment as guidesByAppointment,
  remove as removeGuideRequest,
  printRoute as guidePrintRoute,
  printRequestRoute as guidePrintRequestRoute,
} from '@/app/billing/guide/service';
import GuideModal from '@/app/billing/guide/modals/GuideModal.vue';
import ReferralModal from '../components/modals/Referral.vue';
import ReverseModal from '../../cash-register/modals/Reverse.vue';

export const SERVICE_STATUS_NAMES = {
  scheduled: 'Agendado',
  guide: 'Lançado',
  pending: 'Aguardando caixa',
  paid: 'Pago',
  courtesy: 'Cortesia',
};

export const SERVICE_STATUS_ICONS = {
  scheduled: 'clock',
  guide: 'check-circle',
  pending: 'cash-register',
  paid: 'check-circle',
  courtesy: 'gift',
};

const WAITING_FROM = ['scheduled', 'confirmed', 'payment', 'pending', 'rescheduled'];

function isParticular(insurance) {
  if (!insurance) return false;
  const planType = insurance.plan ? insurance.plan.type : null;
  return planType === 'particular'
    || insurance.type === 'particular'
    || /^particular$/i.test(insurance.name || '');
}

function payerName(insurance) {
  const planName = insurance.plan ? insurance.plan.name : '';
  if (!planName || planName === insurance.name) return insurance.name;
  return `${insurance.name} · ${planName}`;
}

function payerOfInsurance(insurance) {
  if (!insurance || !insurance.plan || !insurance.plan.id) return null;
  const particular = isParticular(insurance);
  return {
    id: insurance.plan.id,
    name: particular ? 'Particular' : payerName(insurance),
    insurance,
    particular,
  };
}

export default {
  components: {
    ReferralModal,
    ReverseModal,
    FiscalDocument,
    GuideModal,
  },
  data() {
    return {
      source: 'appointments',
      printing: false,
      editing: false,
      account: {
        type: '',
        data: {},
        show: false,
      },
      reverse: {
        show: false,
        entry: null,
      },
      deleting: false,
      fiscalDocument: {
        loading: false,
        show: false,
        origin: null,
        totalAmount: 0,
        entity: null,
      },
      guides: {},
      loadingGuides: false,
      guide: {
        show: false,
        accountId: null,
        type: 'sadt',
        insurance: null,
        preselectedExpenseIds: [],
      },
    };
  },
  computed: {
    ...mapState({
      form: state => state.appointment.form,
      user: state => state.auth.user,
      insurances: state => state.appointment.insurances,
      availablePlans: state => state.appointment.available.insurancePlans,
    }),
    isFinished() {
      const settings = this.user.branch.settings || {};
      const allowEdit = !!settings['clinical.allowEditFinishedAppointment'];
      return !!this.form.service.finishedAt && !allowEdit;
    },
    isFiscalDocumentEnabled() {
      const settings = this.user.branch.settings && this.user.branch.settings.fiscalDocument;
      return !!(settings && settings.enabled);
    },
    insuranceBillingEnabled() {
      return isInsuranceBillingEnabled(this.user);
    },
    payers() {
      const result = [];
      const push = (insurance) => {
        const payer = payerOfInsurance(this.resolveInsurance(insurance));
        if (payer && !result.some(({ id }) => id === payer.id)) {
          result.push(payer);
        }
      };
      push(this.form.insurance);
      (this.form.expenses || []).forEach(item => push(item.insurance));
      (this.insurances || [])
        .filter(({ plan }) => this.availablePlans.length === 0
          || this.availablePlans.includes(plan.id))
        .forEach(push);
      return result.sort((a, b) => {
        if (a.particular !== b.particular) return a.particular ? 1 : -1;
        return a.name.localeCompare(b.name);
      });
    },
    serviceItems() {
      return (this.form.expenses || []).map((item, index) => {
        const key = item.id || item.key || `new-${index}`;
        const quantity = item.quantity || 1;
        const paid = !!item.cashRegisterEntryId;
        const billing = item.billing || {};
        const guideId = idOf(billing.accountId);
        const billingStatus = guideId ? billing.status || 'open' : null;
        const billed = !!(guideId || item.accountId);
        const payerId = item.insurance && item.insurance.plan ? item.insurance.plan.id : null;
        const particularPayer = !!this.payerOf({ payerId }).particular;
        let status = 'scheduled';
        if (this.insuranceBillingEnabled) {
          if (billingStatus) status = 'guide';
          else if (paid && particularPayer) status = 'paid';
        } else {
          if (billed) status = 'guide';
          if (paid) status = 'paid';
        }
        const particularTotal = Number(item.totalParticular) || 0;
        const copayValue = this.insuranceBillingEnabled && !particularPayer && !item.return
          ? particularTotal : 0;
        return {
          key,
          source: item,
          code: item.expense ? item.expense.code : '',
          name: item.expense ? item.expense.name : '',
          type: item.expense ? item.expense.type : '',
          typeName: item.expense ? expenseTypeName(item.expense.type) || item.expense.type : '',
          payerId,
          quantity,
          return: !!item.return,
          particularValue: particularTotal / quantity,
          insuranceValue: (Number(item.totalInsurance) || 0) / quantity,
          status,
          guideId,
          billingStatus,
          paid,
          copay: {
            value: copayValue,
            pending: copayValue > 0 && !paid,
            paid: copayValue > 0 && paid,
          },
          locked: billed || paid,
        };
      });
    },
  },
  methods: {
    ...mapMutations({
      setAccount: 'Appointment.SET_ACCOUNT',
      applyBillingSnapshotMutation: 'Appointment.APPLY_BILLING_SNAPSHOT',
    }),
    resolveInsurance(insurance) {
      const planId = insurance && insurance.plan ? insurance.plan.id : null;
      if (!planId) return null;
      const found = (this.insurances || []).find(({ plan }) => plan.id === planId) || {};
      const foundPlan = found.plan || {};
      return {
        id: found.id || insurance.id,
        name: found.name || insurance.name,
        type: found.type || insurance.type,
        plan: {
          id: planId,
          name: foundPlan.name || insurance.plan.name,
          type: foundPlan.type || insurance.plan.type,
        },
      };
    },
    payerOf(item) {
      return this.payers.find(({ id }) => id === item.payerId) || this.payers[0] || {
        id: null, name: '-', particular: true,
      };
    },
    setPayer(item, payerId) {
      const payer = this.payers.find(({ id }) => id === payerId);
      if (!payer) return;
      const { insurance } = payer;
      this.$set(item.source, 'insurance', {
        id: insurance.id,
        name: insurance.name,
        type: insurance.type,
        plan: {
          id: insurance.plan.id,
          name: insurance.plan.name,
          type: insurance.plan.type,
        },
      });
    },
    itemTotal(item) {
      if (item.return) return 0;
      const payer = this.payerOf(item);
      const unit = payer.particular ? item.particularValue : item.insuranceValue;
      return unit * item.quantity;
    },
    insuranceTotal(item) {
      if (item.return || this.payerOf(item).particular) return 0;
      return item.insuranceValue * item.quantity;
    },
    particularTotal(item) {
      if (item.return) return 0;
      return item.particularValue * item.quantity;
    },
    statusName(status) {
      return SERVICE_STATUS_NAMES[status] || status;
    },
    statusIcon(status) {
      return SERVICE_STATUS_ICONS[status] || 'circle';
    },
    guideOf(item) {
      return item.guideId ? this.guides[item.guideId] || null : null;
    },
    guideStatusText(item) {
      const guide = this.guideOf(item);
      const batchNumber = guide && guide.batch ? guide.batch.number : null;
      if (item.billingStatus === 'open') return getGuideStatusLabel('open');
      if (item.billingStatus === 'in_batch') return getGuideStatusLabel('in_batch', batchNumber);
      if (item.billingStatus === 'closed') return getGuideStatusLabel('closed', batchNumber);
      return '';
    },
    lockTooltip(item) {
      const guide = this.guideOf(item);
      const number = guide && guide.batch ? guide.batch.number : '';
      if (item.billingStatus === 'in_batch') return `No lote nº ${number}. Retire do lote para editar`;
      if (item.billingStatus === 'closed') return `Guia encerrada com o lote nº ${number}`;
      return 'Abrir guia';
    },
    async loadGuides() {
      if (!this.insuranceBillingEnabled || !this.form.id) return;
      this.loadingGuides = true;
      try {
        const { items } = await guidesByAppointment(this.form.id);
        const guides = {};
        items.forEach((guide) => {
          guides[guide.id] = guide;
        });
        this.guides = guides;
      } catch (e) {
        this.guides = {};
      } finally {
        this.loadingGuides = false;
      }
    },
    appointmentForGuide() {
      const { form } = this;
      const specialty = form.professional.specialty && typeof form.professional.specialty === 'object'
        ? form.professional.specialty.code
        : form.professional.specialty;
      return {
        id: form.id,
        startDate: form.startDate,
        endDate: form.endDate,
        patient: {
          id: form.patient.id,
          name: form.patient.name,
          cns: form.patient.cns,
          socialName: form.patient.socialName,
        },
        professional: {
          id: form.professional.id,
          name: form.professional.name,
          council: form.professional.council,
          specialty,
        },
        insurance: form.insurance,
        expenses: form.expenses,
      };
    },
    openGuideModal({
      accountId = null, type = 'sadt', insurance = null, expenseIds = [],
    }) {
      if (!accountId && !this.form.id) {
        this.$toast.error('Salve o agendamento antes de registrar.');
        return;
      }
      this.guide = {
        show: true,
        accountId,
        type,
        insurance,
        preselectedExpenseIds: expenseIds,
      };
    },
    closeGuideModal() {
      this.guide.show = false;
    },
    applyBillingSnapshot(appointments) {
      if (!appointments || appointments.length === 0) return;
      this.applyBillingSnapshotMutation(appointments);
      this.syncAccount();
    },
    guideSaved(account) {
      const { appointments, ...guide } = account;
      this.$set(this.guides, guide.id, guide);
      this.applyBillingSnapshot(appointments);
    },
    guideDeleted({ id, appointments }) {
      this.$delete(this.guides, id);
      this.applyBillingSnapshot(appointments);
    },
    printGuide(accountId) {
      this.printing = true;
      return this.$file.print(guidePrintRoute(accountId))
        .catch(() => {})
        .finally(() => {
          this.printing = false;
        });
    },
    printGuideRequest(accountId) {
      this.printing = true;
      return this.$file.print(guidePrintRequestRoute(accountId))
        .catch(() => {})
        .finally(() => {
          this.printing = false;
        });
    },
    async removeGuide(accountId) {
      if (await this.blockedByFiscalDocument(accountId, 'excluir')) return;
      const guide = this.guides[accountId];
      const number = guide ? guide.referral.provider : '';
      this.$dialog.show('', {
        html: '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + `<div>Deseja realmente excluir a guia nº ${number}? `
          + 'Os itens voltam a ficar pendentes no agendamento.</div></div>',
        buttons: [
          { label: 'Não', classes: '' },
          {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              close();
              this.confirmRemoveGuide(accountId);
            },
          },
        ],
      });
    },
    async confirmRemoveGuide(accountId) {
      if (this.deleting) return;
      this.deleting = true;
      try {
        const result = await removeGuideRequest(accountId);
        this.$toast.success('Guia excluída.');
        this.guideDeleted({ id: accountId, appointments: result.appointments || [] });
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.deleting = false;
      }
    },
    findInsurancePlan(insuranceId, planId) {
      return this.$httpX.get(`/insurances/${insuranceId}/plans/${planId}`)
        .then(({ data }) => data)
        .catch(() => null);
    },
    async blockedByFiscalDocument(id, action) {
      if (!this.isFiscalDocumentEnabled) return false;
      const has = await hasIssuedFiscalDocument(this.$http, 'billing_account', id);
      if (has) {
        this.$dialog.show('Ação não permitida', {
          type: 'danger',
          html: 'Esta conta possui uma nota fiscal emitida. '
            + `Cancele a nota fiscal antes de ${action} a conta.`,
        });
      }
      return has;
    },
    printAccount(accountId) {
      this.printing = true;
      return this.$fileX.print(`/legacy/billing-accounts/${accountId}/print`)
        .catch(() => {})
        .finally(() => {
          this.printing = false;
        });
    },
    printRequest(accountId) {
      this.printing = true;
      return this.$fileX.print(`/legacy/billing-accounts/${accountId}/printRequest`)
        .catch((e) => {
          const message = e.response && e.response.data && e.response.data.message
            ? e.response.data.message
            : 'Não foi possível realizar a impressão!';
          this.$toast.error(message, { timeout: 5000 });
        })
        .finally(() => {
          this.printing = false;
        });
    },
    async editAccount(accountId) {
      if (await this.blockedByFiscalDocument(accountId, 'editar')) return;
      this.editing = true;
      try {
        const { data } = await this.$httpX.get(`/legacy/billing-accounts/${accountId}`);
        const professional = data.expenses.length > 0 ? data.expenses[0].professional : null;
        if (data.invoice && data.invoice.id) {
          data.invoiceId = data.invoice.id;
        }
        data.professionalId = professional ? professional.id : null;
        data.specialtyCode = professional && professional.specialty
          ? professional.specialty.code
          : null;
        data.key = this.form.key;
        data.expenses.forEach((expense) => {
          expense.accountId = accountId;
          expense.execution = {
            date: moment(expense.startDate).format('YYYY-MM-DD'),
            time: moment(expense.startDate).format('HH:mm'),
            duration: moment.duration(moment(expense.endDate)
              .diff(moment(expense.startDate))).asMinutes(),
          };
          expense.rules = {
            requireAuthorization: false,
            requireClinicalIndication: false,
            requireWayTechnique: false,
          };
        });

        const { insurance } = data.patient;
        const plan = await this.findInsurancePlan(insurance.id, insurance.plan.id);
        const tiss = plan && plan.settings && plan.settings.tiss;

        if (tiss && tiss.enabled) {
          const params = { expenseIds: data.expenses.map(({ id }) => id).join(',') };
          const { data: rules } = await this.$httpX
            .get(`/expense-insurances/${insurance.plan.id}/rules`, { params });
          (rules.items || []).forEach((item) => {
            data.expenses.forEach((expense) => {
              if (item.id === expense.id && item.rules) {
                expense.rules = item.rules;
              }
            });
          });
          this.account.type = tiss.consultationInSadt ? 'sadt' : data.referral.type;
        } else {
          this.account.type = (plan && plan.type) || insurance.plan.type || 'referral';
        }
        this.account.data = data;
        this.account.show = true;
      } catch (e) {
        this.$toast.error(e);
      }
      this.editing = false;
    },
    hideAccountModal() {
      this.account.show = false;
    },
    openFiscalDocument(originType, originId, totalAmount) {
      this.fiscalDocument.totalAmount = totalAmount;
      this.fiscalDocument.entity = this.form.patient;
      this.fiscalDocument.origin = { type: originType, id: originId };
      this.fiscalDocument.show = true;
    },
    closeFiscalDocument() {
      this.fiscalDocument.totalAmount = 0;
      this.fiscalDocument.entity = null;
      this.fiscalDocument.origin = null;
      this.fiscalDocument.show = false;
    },
    async removeAccount(accountId) {
      if (await this.blockedByFiscalDocument(accountId, 'excluir')) return;
      this.$dialog.show('', {
        html: '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + '<div>Deseja realmente cancelar esta conta e estornar os serviços realizados?</div>'
          + '</div>',
        buttons: [
          { label: 'Não', classes: '' },
          {
            label: 'Sim',
            classes: 'btn-error ml-2',
            click: (close) => {
              if (this.deleting) return Promise.resolve();
              this.deleting = true;
              return this.$httpX.delete(`/legacy/billing-accounts/${accountId}`)
                .then(() => {
                  this.form.status = 'scheduled';
                  this.form.expenses.forEach((expense) => {
                    if (expense.accountId === accountId) {
                      expense.accountId = null;
                      expense.paid = false;
                      expense.selected = false;
                    }
                  });
                  this.syncAccount();
                  close();
                })
                .catch((e) => {
                  const message = e.response && e.response.data && e.response.data.message;
                  if (message) {
                    this.$toast.show(message, { type: 'error', timeout: 5000 });
                  }
                  close();
                })
                .finally(() => {
                  this.deleting = false;
                });
            },
          },
        ],
      });
    },
    printReceipt(entryId) {
      this.printing = true;
      return this.$file.print(`/cash-register/entries/${entryId}/receipt`)
        .catch(() => {})
        .finally(() => {
          this.printing = false;
        });
    },
    async openReverse(entryId) {
      try {
        const { data } = await this.$http.get(`/cash-register/entries/${entryId}`);
        this.reverse.entry = data;
        this.reverse.show = true;
      } catch (e) {
        this.$toast.error(e);
      }
    },
    closeReverse(saved) {
      this.reverse.show = false;
      if (saved && this.reverse.entry) {
        const entryId = this.reverse.entry.id;
        this.form.expenses.forEach((expense) => {
          if (expense.cashRegisterEntryId === entryId) {
            expense.cashRegisterEntryId = null;
            expense.paid = false;
          }
        });
        if (this.form.status === 'waiting') {
          this.form.status = 'scheduled';
        }
        this.syncAccount();
      }
      this.reverse.entry = null;
    },
    applyReceipt(entry) {
      const ids = (entry.items || []).map(item => String(item.appointmentExpenseId));
      this.form.expenses.forEach((expense) => {
        if (ids.includes(String(expense.id))) {
          expense.cashRegisterEntryId = entry.id;
          expense.paid = true;
        }
      });
      if (WAITING_FROM.includes(this.form.status)) {
        this.form.status = 'waiting';
      }
      this.syncAccount();
    },
    syncAccount() {
      this.setAccount({
        key: this.form.key,
        expenses: this.form.expenses,
      });
    },
  },
};
